import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { useAppSelector } from "../hooks";

export interface CorporatePassengers {
  adults: number;
  teenagers: number;
  children: number;
  infants: number;
}

export interface CorporateDates {
  from?: string;
  to?: string;
}

export interface CorporateSearchState {
  departure: string;
  departureCountryCode: string;
  departureCode: string;
  departureCityCode: string;
  destination: string;
  destinationCode: string;
  destinationCountryCode: string;
  destinationCityCode: string;
  travelDates: CorporateDates;
  passengers: CorporatePassengers;
  inboundSubtotal: number;
  outboundSubtotal: number;
  useAAdvantageMiles: boolean;
  promoCode?: string;
  isRoundtrip?: boolean;
}

const initialState: CorporateSearchState = {
  departure: "",
  departureCode: "",
  departureCountryCode: "",
  departureCityCode: "",
  destination: "",
  destinationCode: "",
  destinationCountryCode: "",
  destinationCityCode: "",
  travelDates: {},
  passengers: { adults: 0, teenagers: 0, children: 0, infants: 0 },
  inboundSubtotal: 0,
  outboundSubtotal: 0,
  useAAdvantageMiles: false,
  promoCode: "",
  isRoundtrip: undefined,
};

const corporateSlice = createSlice({
  name: "corporate",
  initialState,
  reducers: {
    setCorporateSearch: (state, action: PayloadAction<CorporateSearchState>) => {
      state.departure = action.payload.departure;
      state.departureCode = action.payload.departureCode ?? "";
      state.departureCountryCode = action.payload.departureCountryCode ?? "";
      state.departureCityCode = action.payload.departureCityCode ?? "";
      state.destination = action.payload.destination;
      state.destinationCode = action.payload.destinationCode ?? "";
      state.destinationCountryCode = action.payload.destinationCountryCode ?? "";
      state.destinationCityCode = action.payload.destinationCityCode ?? "";
      state.travelDates = action.payload.travelDates ?? {};
      state.passengers = action.payload.passengers;
      state.inboundSubtotal = action.payload.inboundSubtotal ?? 0;
      state.outboundSubtotal = action.payload.outboundSubtotal ?? 0;
      state.useAAdvantageMiles = action.payload.useAAdvantageMiles ?? false;
      state.promoCode = action.payload.promoCode ?? "";
      state.isRoundtrip = action.payload.isRoundtrip;
    },
    clearCorporateSearch: (state) => {
      state.departure = "";
      state.departureCode = "";
      state.departureCountryCode = "";
      state.departureCityCode = "";
      state.destination = "";
      state.destinationCode = "";
      state.destinationCountryCode = "";
      state.destinationCityCode = "";
      state.travelDates = {};
      state.passengers = { adults: 0, teenagers: 0, children: 0, infants: 0 };
      state.inboundSubtotal = 0;
      state.outboundSubtotal = 0;
      state.useAAdvantageMiles = false;
      state.promoCode = "";
      state.isRoundtrip = false;
    },
    clearSubtotal: (state) => {
      state.inboundSubtotal = 0;
      state.outboundSubtotal = 0;
    },
    setInboundSubtotal: (state, action: PayloadAction<number>) => {
      state.inboundSubtotal = action.payload;
    },
    setOutboundSubtotal: (state, action: PayloadAction<number>) => {
      state.outboundSubtotal = action.payload;
    },
    setCorporateTravelDates: (state, action: PayloadAction<Partial<CorporateDates>>) => {
      state.travelDates = { ...state.travelDates, ...action.payload };
    },
    setPromoCode: (state, action: PayloadAction<string>) => {
      state.promoCode = action.payload;
    },
    setDeparture: (
      state,
      action: PayloadAction<{
        departure: string;
        departureCode: string;
        departureCountryCode: string;
        departureCityCode: string;
      }>,
    ) => {
      state.departure = action.payload.departure;
      state.departureCode = action.payload.departureCode;
      state.departureCountryCode = action.payload.departureCountryCode;
      state.departureCityCode = action.payload.departureCityCode;
    },
    setDestination: (
      state,
      action: PayloadAction<{
        destination: string;
        destinationCode: string;
        destinationCountryCode: string;
        destinationCityCode: string;
      }>,
    ) => {
      state.destination = action.payload.destination;
      state.destinationCode = action.payload.destinationCode;
      state.destinationCountryCode = action.payload.destinationCountryCode;
      state.destinationCityCode = action.payload.destinationCityCode;
    },
    setPassengersCount: (state, action: PayloadAction<CorporatePassengers>) => {
      state.passengers = action.payload;
    },
    setMiles: (state, action: PayloadAction<boolean>) => {
      state.useAAdvantageMiles = action.payload;
    },
    setIsRoundtrip: (state, action: PayloadAction<boolean>) => {
      state.isRoundtrip = action.payload;
    },
  },
});

// Selectors/hooks
export const useCorporateSearch = () => useAppSelector((state) => state.corporate);
export const useCorporateDeparture = () => useAppSelector((state) => state.corporate.departure);
export const useCorporateDepartureCode = () =>
  useAppSelector((state) => state.corporate.departureCode);
export const useCorporateDepartureCountryCode = () =>
  useAppSelector((state) => state.corporate.departureCountryCode);
export const useCorporateDepartureCityCode = () =>
  useAppSelector((state) => state.corporate.departureCityCode);
export const useCorporateDestination = () => useAppSelector((state) => state.corporate.destination);
export const useCorporateDestinationCode = () =>
  useAppSelector((state) => state.corporate.destinationCode);
export const useCorporateDestinationCountryCode = () =>
  useAppSelector((state) => state.corporate.destinationCountryCode);
export const useCorporateDestinationCityCode = () =>
  useAppSelector((state) => state.corporate.destinationCityCode);
export const useCorporatePassengers = () => useAppSelector((state) => state.corporate.passengers);
export const useCorporateTotalPassengers = () =>
  useAppSelector(
    (state) => state.corporate.passengers.adults + state.corporate.passengers.children,
  );
export const useCorporateDates = () => useAppSelector((state) => state.corporate.travelDates);

export const useCorporateInboundSubtotal = () =>
  useAppSelector((state) => state.corporate.inboundSubtotal);

export const useCorporateOutboundSubtotal = () =>
  useAppSelector((state) => state.corporate.outboundSubtotal);

export const useCorporateTotalSubtotal = (): number =>
  useAppSelector((state) => state.corporate.inboundSubtotal + state.corporate.outboundSubtotal);
export const useCorporateMiles = () =>
  useAppSelector((state) => state.corporate.useAAdvantageMiles);

export const useCorporatePromoCode = () => useAppSelector((state) => state.corporate.promoCode);
export const useCorporateIsRoundtrip = () => useAppSelector((state) => state.corporate.isRoundtrip);

export const {
  setCorporateSearch,
  clearCorporateSearch,
  clearSubtotal,
  setInboundSubtotal,
  setOutboundSubtotal,
  setCorporateTravelDates,
  setPromoCode,
  setDeparture,
  setDestination,
  setPassengersCount,
  setMiles,
  setIsRoundtrip,
} = corporateSlice.actions;
export default corporateSlice.reducer;
